import { LsGarmentPair } from '@/components/ls-garment-pair/LsGarmentPair';
import type { CartItem } from '@/features/cart/cart.store';
import { usePetStore } from '@/features/pet/pet.store';
import { LsPetPrint } from '@/screens/pet/LsPetPrint';
import { garmentOf } from '@/shared/content/garments';
import styles from './ls-cart-thumb.module.scss';

interface Props {
  item: CartItem;
}

/**
 * Миниатюра позиции заказа. Одна на корзину и на состав заказа: в двух
 * местах позиция обязана выглядеть одинаково, иначе человек решит, что
 * это разные вещи.
 *
 * Показываем ровно то, что человек выбрал: изделие в его цвете и его же
 * принт. Собранный в конструкторе принт рисуется только у своей позиции —
 * на готовом изделии это был бы чужой рисунок. Ни заглушек, ни серых
 * прямоугольников: пустая плашка читается как «картинка не загрузилась».
 */
export function LsCartThumb({ item }: Props) {
  const livePetUrl = usePetStore((s) => s.cutoutPrintUrl);

  // Готовый снимок собранной вещи сильнее любой пересборки: он показывает
  // именно то, что человек утвердил.
  if (item.previewUrl) {
    return (
      <span className={styles['ls-cart-thumb']}>
        <img
          className={styles['ls-cart-thumb__print']}
          src={item.previewUrl}
          alt=""
          draggable={false}
        />
      </span>
    );
  }

  // Собранный принт питомца берётся из файла, если он есть. Живая сборка
  // (LsPetPrint) читает состояние конструктора, а его нет там, куда
  // корзина переехала: в мини-приложении после сайта футболка была пустой.
  // Готовый файл с кличкой не собрался, а снимок — тот, что сейчас
  // в конструкторе: показываем живую сборку с кличкой, а не голого питомца.
  const isLivePet = item.isCustom && Boolean(item.printUrl) && item.printUrl === livePetUrl;

  const print = isLivePet ? (
    <LsPetPrint />
  ) : item.printUrl ? (
    <img className={styles['ls-cart-thumb__print']} src={item.printUrl} alt="" draggable={false} />
  ) : item.isCustom ? (
    <LsPetPrint />
  ) : null;

  return (
    <span className={styles['ls-cart-thumb']}>
      <LsGarmentPair
        type={garmentOf('tshirt').type}
        color={item.color}
        front={print}
        only="front"
        isBare
      />
    </span>
  );
}
